import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DisplayPredictions from './components/DisplayPredictions';
import DocumentUpload from '../../modals/DocumentUpload';
import { getDocument, deletePage, getDocumentPages } from '../../utils/Api';
import { Search, FileText, Share2, PlusCircle, Trash2 } from 'lucide-react';

interface DocumentData {
  name: string;
  [key: string]: any;
}

interface PagesData {
  [pageID: string]: any;
}

const View: React.FC = () => {
  const { id: documentID } = useParams<{ id: string }>();
  const [documentName, setDocumentName] = useState<string>('Loading...');
  const [addPagesModalOpen, setAddPagesModalOpen] = useState<boolean>(false);
  const [pages, setPages] = useState<string[]>([]);
  const [pageID, setPageID] = useState<string | null>(null);

  useEffect(() => {
    if (!documentID) return;

    const fetchDocument = async () => {
      try {
        const { data: document }: { data: DocumentData } = await getDocument(documentID);
        setDocumentName(document.name);

        const { data: pagesData }: { data: PagesData } = await getDocumentPages(documentID);
        const pageIds = Object.keys(pagesData);
        setPages(pageIds);
        setPageID(pageIds[0] || null);
      } catch (error) {
        console.error('Error fetching document:', error);
      }
    };

    fetchDocument();
  }, [documentID]);

  return (
    <>
      <div className="flex-grow flex flex-col md:flex-row bg-slate-50 text-slate-800 font-sans">
        <div className="w-full md:w-1/3 lg:w-1/4 xl:w-1/5 bg-white border-r border-slate-200 flex flex-col p-6 space-y-6">
          <div className="flex items-center space-x-3">
            <FileText className="text-blue-500" size={28} />
            <h1 className="font-bold text-xl text-slate-700 truncate" title={documentName}>
              {documentName}
            </h1>
          </div>

          <div className="w-full">
            <label htmlFor="search" className="text-sm font-semibold text-slate-700 mb-2 block">
              Search the Document
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                id="search"
                type="text"
                placeholder="Query"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition"
              />
            </div>
          </div>

          <div className="flex-grow flex flex-col">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Pages</h2>
            <div className="w-full flex-grow bg-slate-100/70 rounded-lg p-3 grid grid-flow-row auto-rows-min grid-cols-4 sm:grid-cols-5 md:grid-cols-4 lg:grid-cols-5 gap-3 overflow-y-auto h-48 border border-slate-200">
              {pages.map((id, index) => (
                <div
                  key={id}
                  className={`aspect-square flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200 transform hover:scale-105
                    ${pageID === id
                      ? 'bg-blue-500 text-white shadow-md ring-2 ring-offset-2 ring-blue-500'
                      : 'bg-white hover:bg-blue-100 text-slate-700'
                    }`}
                  onClick={() => setPageID(id)}
                >
                  <span className="text-lg font-semibold">{index + 1}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full space-y-3">
            <h2 className="text-sm font-semibold text-slate-700">Page Actions</h2>
            <button className="w-full flex items-center justify-center space-x-2 bg-blue-500 text-white font-semibold py-2.5 rounded-lg hover:bg-blue-600 transition-colors">
              <Share2 size={20} />
              <span>Export Data</span>
            </button>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                className="w-full flex items-center justify-center space-x-2 bg-blue-100 text-blue-800 font-semibold py-2 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                onClick={() => setAddPagesModalOpen(true)}
              >
                <PlusCircle size={18} />
                <span>Add</span>
              </button>
              <button
                className="w-full flex items-center justify-center space-x-2 bg-red-100 text-red-800 font-semibold py-2 rounded-lg hover:bg-red-200 transition-colors text-sm"
                onClick={() => pageID && deletePage(pageID)}
              >
                <Trash2 size={18} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-grow flex flex-col items-center justify-center bg-slate-50 overflow-hidden">
          <div className="w-full h-full relative">
            <DisplayPredictions pageID={pageID} />
          </div>
        </div>
      </div>

      <DocumentUpload
        isOpen={addPagesModalOpen}
        setIsOpen={setAddPagesModalOpen}
        mode="add"
        documentID={documentID}
      />
    </>
  );
};

export default View;
